'use client'

/**
 * Route-level error boundary for the /mycio subtree — contained AND reported.
 *
 * Until this existed there was NO error.tsx anywhere: any render crash in any component
 * escalated to global-error.tsx and replaced the ENTIRE app with "Something went wrong"
 * (2026-08-22: one non-string narrative field took down the whole plan page for every
 * staged-recompute plan). This boundary keeps a crash inside the mycio segment — the top
 * bar and the rest of the shell stay alive — and reports it to Sentry, which is the other
 * half of the same incident: the global boundary was capturing into an unset DSN, so the
 * crash left no trace and had to be reproduced from scratch, twice.
 */

import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'
import { MC, SANS } from '@/lib/mycio/theme'
import { useMyCIOLanguageOrCookie } from '@/lib/mycio/MyCIOLanguageContext'
import { getMyCIOShellChromeCopy } from '@/lib/mycio/copy/shellChrome'

export default function MyCIOError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const t = getMyCIOShellChromeCopy(useMyCIOLanguageOrCookie()).error
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div style={{ fontFamily: SANS, background: MC.bg, minHeight: '60vh', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}>
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <h2 style={{ fontSize: 20, fontWeight: 650, color: MC.ink, margin: '0 0 10px' }}>
          {t.title}
        </h2>
        <p style={{ fontSize: 14.5, lineHeight: 1.55, color: MC.muted, margin: '0 0 18px' }}>
          {t.body}
        </p>
        <button
          onClick={() => reset()}
          style={{ fontFamily: SANS, padding: '10px 18px', borderRadius: 10, cursor: 'pointer',
                   border: `1px solid ${MC.border}`, background: MC.ink, color: MC.bg,
                   fontSize: 14.5, fontWeight: 600 }}
        >
          {t.tryAgain}
        </button>
      </div>
    </div>
  )
}
